import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { UseFormReturnType, useForm } from "@mantine/form";
import { addressFormValidator, addressFormValues } from "../forms/address";
import { createAddress, findUserAddresses } from "@/services/address";
import { signIn, signUp } from "@/services/auth";
import { signInFormValidator, signInFormValues } from "../forms/signIn";
import { signUpFormValidator, signUpFormValues } from "../forms/signUp";

import { FindUserAddressesResponse } from "@/types/api/address";
import { PaymentStep } from "@/types/pages/form";
import { ShippingQuote } from "@/types/api/shipment";
import { attachUserToQuote } from "@/services/quote";
import { clearString } from "@/utils/formatters/clearString";
import { findAddressByCep } from "@/services/viacep";
import { findShipmentQuotes } from "@/services/shipment";
import { formatStringDates } from "@/utils/formatters/formatStringDates";
import { getErrorMessage } from "@/utils/errorMessage";
import { findMeSilent } from "@/services/user";
import { findPaymentStatus, paymentCheckout } from "@/services/payment";
import { removeSpecialCharsAndSpaces } from "@/utils/formatters/removeSpecialCharsAndSpaces";
import { showErrorNotification } from "@/utils/notifications/showErrorNotification";
import { showSuccessNotification } from "@/utils/notifications/showSuccessNotification";
import { useNavigate } from "react-router-dom";

interface UseOrderFormProps {
  nextStep: (step?: PaymentStep) => void;
  quoteId: number;
  setShippingAmount: (value: number) => void;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

interface UseOrderFormResponse {
  userAddresses: FindUserAddressesResponse[];
  shipmentQuotes: ShippingQuote[];
  isPaymentLinkOpened: boolean;
  setIsPaymentLinkOpened: Dispatch<SetStateAction<boolean>>;
  selectedAddress: FindUserAddressesResponse | null;
  setSelectedAddress: (addressId: FindUserAddressesResponse) => void;
  selectedShipmentQuote: ShippingQuote | null;
  setSelectedShipmentQuote: (quote: ShippingQuote) => void;
  signInForm: UseFormReturnType<typeof signInFormValues>;
  handleSubmitSignIn: (values: typeof signInFormValues) => Promise<void>;
  signUpForm: UseFormReturnType<typeof signUpFormValues>;
  handleSubmitSignUp: (values: typeof signUpFormValues) => Promise<void>;
  addressForm: UseFormReturnType<typeof addressFormValues>;
  handleSubmitAddress: (values: typeof addressFormValues) => Promise<void>;
  accountType: "signUp" | "signIn";
  setAccountType: Dispatch<SetStateAction<"signUp" | "signIn">>;
  isCreateAddressModalOpen: boolean;
  setIsCreateAddressModalOpen: Dispatch<SetStateAction<boolean>>;
  handleFindAddressByCep: (cep: string) => Promise<void>;
  handleFindShipmentQuotes: () => Promise<void>;
  handleAcceptShipmentQuote: () => void;
  handleOpenPaymentLink: () => void;
  handleConfirmPayment: () => Promise<void>;
}

export const useOrderForm = ({
  nextStep,
  quoteId,
  setShippingAmount,
  setIsLoading,
}: UseOrderFormProps): UseOrderFormResponse => {
  const signInForm = useForm({
    initialValues: signInFormValues,
    validate: signInFormValidator,
    validateInputOnBlur: true,
  });

  const signUpForm = useForm({
    initialValues: signUpFormValues,
    validate: signUpFormValidator,
    validateInputOnBlur: true,
  });

  const addressForm = useForm({
    initialValues: addressFormValues,
    validate: addressFormValidator,
    validateInputOnBlur: true,
  });

  const [isPaymentLinkOpened, setIsPaymentLinkOpened] = useState(false);
  const [accountType, setAccountType] = useState<"signUp" | "signIn">("signUp");
  const [selectedAddress, setSelectedAddress] =
    useState<FindUserAddressesResponse | null>(null);
  const [userAddresses, setUserAddresses] = useState<FindUserAddressesResponse[]>([]);
  const [isCreateAddressModalOpen, setIsCreateAddressModalOpen] = useState(false);
  const [shipmentQuotes, setShipmentQuotes] = useState<ShippingQuote[]>([]);
  const [selectedShipmentQuote, setSelectedShipmentQuote] = useState<ShippingQuote | null>(null);
  const [paymentCheckoutUrl, setPaymentCheckoutUrl] = useState("");
  const [paymentId, setPaymentId] = useState<number | null>(null);

  const navigate = useNavigate();

  const handleSubmitSignIn = async (values: typeof signInFormValues) => {
    setIsLoading(true);

    try {
      await signIn({ email: values.email, password: values.password });
    } catch (err: any) {
      const message = getErrorMessage(err.code);
      showErrorNotification("Erro ao fazer login: " + message);
      setIsLoading(false);
      return;
    }

    try {
      await attachUserToQuote({ quoteId });
    } catch (err: any) {
      const message = getErrorMessage(err.code);
      showErrorNotification("Erro ao vincular orçamento: " + message);
      setIsLoading(false);
      return;
    }

    showSuccessNotification("Login realizado com sucesso!");
    nextStep("shipping");

    setIsLoading(false);
    await handleFindUserAddresses();
  };

  const handleSubmitSignUp = async (values: typeof signUpFormValues) => {
    setIsLoading(true);

    try {
      await signUp({
        firstName: values.firstName,
        lastName: values.lastName,
        birthdate: formatStringDates(values.birthdate),
        phoneNumber: "+55" + removeSpecialCharsAndSpaces(values.phoneNumber),
        email: values.email,
        password: values.password,
        document: removeSpecialCharsAndSpaces(values.document),
      });
    } catch (err: any) {
      const message = getErrorMessage(err.code);
      showErrorNotification("Erro ao criar conta: " + message);
      setIsLoading(false);
      return;
    }

    try {
      await signIn({ email: values.email, password: values.password });
    } catch (err: any) {
      const message = getErrorMessage(err.code);
      showErrorNotification("Erro ao fazer login: " + message);
      setIsLoading(false);
      return;
    }

    try {
      await attachUserToQuote({ quoteId });
    } catch (err: any) {
      const message = getErrorMessage(err.code);
      showErrorNotification("Erro ao vincular orçamento: " + message);
      setIsLoading(false);
      return;
    }

    showSuccessNotification("Conta criada com sucesso!");
    nextStep("shipping");

    setIsLoading(false);
    await handleFindUserAddresses();
  };

  const handleFindUserAddresses = async () => {
    setIsLoading(true);

    try {
      const addresses = await findUserAddresses();
      setUserAddresses(addresses);
    } catch (err: any) {
      showErrorNotification("Erro ao buscar endereços: " + err.message);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    findMeSilent()
      .then(async () => {
        if (cancelled) return;

        try {
          await attachUserToQuote({ quoteId });
        } catch {
          // quote may already be attached to this user; ignore
        }

        nextStep("shipping");
        await handleFindUserAddresses();
      })
      .catch(() => {
        // not authenticated — stay on the account step
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleFindShipmentQuotes = async () => {
    setIsLoading(true);

    try {
      const res = await findShipmentQuotes({
        quoteId,
        zipCode: selectedAddress?.zipCode || "",
      });

      setShipmentQuotes(res.shippingQuotes);
    } catch (err: any) {
      showErrorNotification("Erro ao calcular frete: " + err.message);
    }

    setIsLoading(false);
  };

  const handleSubmitAddress = async (values: typeof addressFormValues) => {
    setIsLoading(true);

    try {
      const res = await createAddress({
        zipCode: values.zipCode,
        street: values.street,
        number: values.number,
        neighborhood: values.neighborhood,
        city: values.city,
        state: values.state,
        complement: values.complement,
      });

      showSuccessNotification("Endereço criado com sucesso!");

      setUserAddresses((prev) => [
        ...prev,
        {
          id: res.id,
          zipCode: values.zipCode,
          street: values.street,
          number: values.number,
          neighborhood: values.neighborhood,
          city: values.city,
          state: values.state,
          complement: values.complement,
          createdAt: new Date().toISOString(),
        },
      ]);

      addressForm.reset();
      setIsCreateAddressModalOpen(false);
    } catch (err: any) {
      showErrorNotification("Erro ao criar endereço: " + err.message);
    }

    setIsLoading(false);
  };

  const handleFindAddressByCep = async (cep: string) => {
    addressForm.setFieldValue("zipCode", cep);

    if (cep.length === 9) {
      setIsLoading(true);

      try {
        const address = await findAddressByCep({ cep: clearString(cep) });

        addressForm.setFieldValue("zipCode", address.cep);
        addressForm.setFieldValue("street", address.logradouro);
        addressForm.setFieldValue("neighborhood", address.bairro);
        addressForm.setFieldValue("city", address.localidade);
        addressForm.setFieldValue("state", address.uf);
      } catch (err: any) {
        showErrorNotification("Erro ao buscar endereço pelo CEP: " + err.message);
      }

      setIsLoading(false);
    }
  };

  const handleChangeSelectedAddress = (address: FindUserAddressesResponse) => {
    setSelectedAddress(address);
    setShipmentQuotes([]);
    setSelectedShipmentQuote(null);
  };

  const handleAcceptShipmentQuote = () => {
    if (selectedShipmentQuote) {
      nextStep("payment");
      handlePaymentCheckout();
    }
  };

  const handleSelectShipmentQuote = (quote: ShippingQuote) => {
    setSelectedShipmentQuote(quote);
    setShippingAmount(quote.shippingPrice);
  };

  const getUtmParamsFromUrl = () => {
    const searchParams = new URLSearchParams(window.location.search);

    return {
      utmSource: searchParams.get("utm_source") || undefined,
      utmMedium: searchParams.get("utm_medium") || undefined,
      utmCampaign: searchParams.get("utm_campaign") || undefined,
      utmTerm: searchParams.get("utm_term") || undefined,
      utmContent: searchParams.get("utm_content") || undefined,
    };
  };

  const handlePaymentCheckout = useCallback(async () => {
    setIsLoading(true);

    try {
      if (!selectedAddress) throw new Error("Endereço inválido");
      if (!selectedShipmentQuote) throw new Error("Frete inválido");

      const { paymentId: newPaymentId, paymentLinkUrl } = await paymentCheckout({
        quoteId,
        addressId: selectedAddress.id,
        shippingCode: selectedShipmentQuote.shippingCode,
        ...getUtmParamsFromUrl(),
      });

      setPaymentId(newPaymentId);
      setPaymentCheckoutUrl(paymentLinkUrl);
    } catch (err: any) {
      console.error("handlePaymentCheckout failed:", err);
      showErrorNotification(
        "Erro ao gerar link de pagamento: " +
          (err.message || "tente novamente"),
      );
    }

    setIsLoading(false);
  }, [quoteId, selectedAddress, selectedShipmentQuote]);

  const handleOpenPaymentLink = useCallback(() => {
    if (paymentCheckoutUrl !== "") {
      const newWindow = window.open(paymentCheckoutUrl, "_blank");

      if (!newWindow) {
        showErrorNotification("Habilite pop-ups para continuar com o pagamento");
        return;
      }

      newWindow.focus();
      setIsPaymentLinkOpened(true);
    }
  }, [paymentCheckoutUrl]);

  const handleConfirmPayment = useCallback(async () => {
    if (!paymentId) return;

    setIsLoading(true);

    try {
      const { status } = await findPaymentStatus(paymentId);

      if (status === "succeeded") {
        navigate("/dash/my-plans");
        return;
      }

      showErrorNotification(
        "Pagamento ainda não confirmado. Se você já pagou, aguarde alguns instantes e tente novamente.",
      );
    } catch (err: any) {
      showErrorNotification("Erro ao verificar status do pagamento: " + err.message);
    }

    setIsLoading(false);
  }, [paymentId, navigate, setIsLoading]);

  return {
    userAddresses,
    shipmentQuotes,
    isPaymentLinkOpened,
    setIsPaymentLinkOpened,
    selectedAddress,
    selectedShipmentQuote,
    setSelectedShipmentQuote: handleSelectShipmentQuote,
    setSelectedAddress: handleChangeSelectedAddress,
    signInForm,
    handleSubmitSignIn,
    signUpForm,
    handleSubmitSignUp,
    addressForm,
    handleSubmitAddress,
    accountType,
    setAccountType,
    isCreateAddressModalOpen,
    setIsCreateAddressModalOpen,
    handleFindAddressByCep,
    handleFindShipmentQuotes,
    handleAcceptShipmentQuote,
    handleOpenPaymentLink,
    handleConfirmPayment,
  };
};
